import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Icon, useTheme } from '@strategy4kids/ui';
import { useT } from '../i18n/useT.js';
import { usePlayerLookStore } from '../state/player-look-store.js';
import { useProfileStore } from '../state/profile-store.js';

export interface UserMenuProps {
  readonly onSettings: () => void;
  readonly onParent: () => void;
  readonly onOnline: () => void;
}

/**
 * Round avatar button + dropdown in the topbar. The avatar is the player's buddy ;
 * the menu holds the account-ish actions: settings, parents, online/sign-in, back to the system theme, sign out.
 * Fixed-px chrome like the rest of the topbar. Closes on Escape, outside click and item choice; focus returns to the button.
 */
export function UserMenu({ onSettings, onParent, onOnline }: UserMenuProps) {
  const { t } = useT('navigation');
  const { theme, setTheme } = useTheme();
  const name = usePlayerLookStore((s) => s.name);
  const buddy = usePlayerLookStore((s) => s.buddy);
  const profile = useProfileStore((s) => s.profile);
  const clearProfile = useProfileStore((s) => s.clearProfile);

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, close]);

  // Focus the first item when the menu opens.
  useEffect(() => {
    if (open) menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  const items = (): HTMLElement[] => Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    }
    if (e.key === 'Tab') {
      close(false);
      return;
    }
    const list = items();
    const i = list.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      list[(i + 1) % list.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      list[(i - 1 + list.length) % list.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      list[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      list[list.length - 1]?.focus();
    }
  };

  const choose = (action: () => void) => {
    close(true);
    action();
  };

  const displayName = name.trim();

  return (
    <div ref={rootRef} className="relative shrink-0" data-testid="user-menu" data-appdom-id="user-menu" data-appdom-type="Menu">
      <button
        ref={buttonRef}
        type="button"
        data-testid="user-menu-button"
        data-appdom-id="user-menu-button"
        data-appdom-type="Button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={t('user-menu')}
        title={displayName || t('user-menu')}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className="inline-flex size-[48px] items-center justify-center rounded-full border-2 border-sun/40 bg-berry/15 text-berry transition-colors hover:bg-berry/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-sun/30"
      >
        <Icon name={buddy} size="md" />
      </button>
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={t('user-menu')}
          data-testid="user-menu-panel"
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-[min(18rem,calc(100vw-32px))] overflow-hidden rounded-2xl border border-border bg-surface-raised p-1 text-fg shadow-xl"
        >
          <div className="flex items-center gap-3 border-b border-border px-3 py-2" role="presentation">
            <span aria-hidden="true" className="inline-flex size-[36px] shrink-0 items-center justify-center rounded-full bg-berry/15 text-berry">
              <Icon name={buddy} size="md" />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-display text-base font-bold" data-testid="user-menu-name">{displayName || t('user-menu-guest')}</span>
              {profile && (
                <span className="truncate text-xs font-semibold text-fg-muted" data-testid="user-menu-profile">
                  {t('user-menu-signed-in', { name: profile.name })}
                </span>
              )}
            </span>
          </div>
          <MenuItem testid="user-menu-settings" label={t('sound-and-settings')} onClick={() => choose(onSettings)} />
          <MenuItem testid="user-menu-parent" label={t('for-parents')} onClick={() => choose(onParent)} />
          <MenuItem testid="user-menu-online" label={profile ? t('play-online') : t('user-menu-sign-in')} onClick={() => choose(onOnline)} />
          {theme !== 'system' && (
            <MenuItem testid="user-menu-system-theme" label={t('user-menu-system-theme')} onClick={() => choose(() => setTheme('system'))} />
          )}
          {profile && <MenuItem testid="user-menu-sign-out" label={t('user-menu-sign-out')} onClick={() => choose(clearProfile)} />}
        </div>
      )}
    </div>
  );
}

function MenuItem({ testid, label, onClick }: { readonly testid: string; readonly label: string; readonly onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      data-testid={testid}
      data-appdom-id={testid}
      data-appdom-type="MenuItem"
      onClick={onClick}
      className="flex min-h-[48px] w-full items-center rounded-xl px-3 py-2.5 text-left text-base font-bold text-fg transition-colors hover:bg-sun/20 focus-visible:bg-sun/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {label}
    </button>
  );
}
